import React from 'react';

/**
 * DataTables-style pagination footer.
 *
 * Props:
 *   total           — total number of rows (filtered, before slicing)
 *   entriesPerPage  — rows shown per page
 *   currentPage     — 1-based current page
 *   onChange(page)  — called when the user picks a different page
 *   testIdPrefix    — optional prefix for data-testid attributes
 */
const Pagination = ({ total, entriesPerPage, currentPage, onChange, testIdPrefix = 'pagination' }) => {
  const safeEpp = Math.max(1, parseInt(entriesPerPage, 10) || 1);
  const totalPages = Math.max(1, Math.ceil((total || 0) / safeEpp));
  const page = Math.min(Math.max(1, parseInt(currentPage, 10) || 1), totalPages);

  const from = total === 0 ? 0 : (page - 1) * safeEpp + 1;
  const to = Math.min(total, page * safeEpp);

  // DataTables-style window: first, last, current ±2, with ellipses.
  const buildPageList = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const set = new Set([1, totalPages, page - 1, page, page + 1]);
    const sorted = Array.from(set).filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < sorted.length; i += 1) {
      out.push(sorted[i]);
      if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) out.push('…');
    }
    return out;
  };
  const pageList = buildPageList();

  const go = (p) => {
    if (typeof p !== 'number') return;
    if (p < 1 || p > totalPages || p === page) return;
    onChange(p);
  };

  const baseBtn = 'border border-gray-300 px-3 py-1 text-sm transition-colors';
  const disabledBtn = 'text-gray-400 cursor-not-allowed bg-white';
  const enabledBtn = 'text-gray-700 hover:bg-gray-100 bg-white';
  const activeBtn = 'bg-[#DC3545] border-[#DC3545] text-white';

  return (
    <div
      className="sticky bottom-0 -mx-4 lg:-mx-6 px-4 lg:px-6 py-3 mt-4 bg-white/95 backdrop-blur border-t border-gray-200 shadow-[0_-4px_8px_-4px_rgba(0,0,0,0.08)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 z-30"
      data-testid={`${testIdPrefix}-bar`}
    >
      <div className="text-sm text-gray-600" data-testid={`${testIdPrefix}-summary`}>
        Showing {from} to {to} of {total} entries
      </div>
      <div className="inline-flex rounded overflow-hidden shadow-sm" data-testid={`${testIdPrefix}-buttons`}>
        <button
          type="button"
          onClick={() => go(page - 1)}
          disabled={page === 1}
          className={`${baseBtn} rounded-l ${page === 1 ? disabledBtn : enabledBtn}`}
          data-testid={`${testIdPrefix}-prev`}
        >
          Previous
        </button>
        {pageList.map((p, idx) => (
          p === '…'
            ? (
              <span
                key={`ellipsis-${idx}`}
                className={`${baseBtn} ${disabledBtn} -ml-px`}
              >
                …
              </span>
            )
            : (
              <button
                key={p}
                type="button"
                onClick={() => go(p)}
                className={`${baseBtn} -ml-px ${p === page ? activeBtn : enabledBtn}`}
                data-testid={`${testIdPrefix}-page-${p}`}
              >
                {p}
              </button>
            )
        ))}
        <button
          type="button"
          onClick={() => go(page + 1)}
          disabled={page === totalPages || total === 0}
          className={`${baseBtn} rounded-r -ml-px ${page === totalPages || total === 0 ? disabledBtn : enabledBtn}`}
          data-testid={`${testIdPrefix}-next`}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Pagination;
